import React, { FC, useState, FocusEvent } from 'react';
import clsx from 'clsx';
import TextInput from 'components/TextInput/TextInput';
import { PercentageImage } from 'static/images';
import { DecimalPartLimiter, removeNonNumericChars } from 'components/NumberInput/utils';
import { LoaderState } from 'components/LoaderWithState/LoaderWithState';
import { numberToPercentage, percentageToNumber } from './utils';
import useMask from 'hooks/useMask';
import maskNumberValue from 'utils/masks/maskNumberValue';
import styles from './PercentageInput.module.scss';

interface PercentageInputProps {
  value: string;
  labelTitle: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  showLoader?: boolean;
  loaderState?: LoaderState | null;
  onLoaderStateReset?: () => void;
  tabIndex?: number;
  inputIcon?: React.ReactNode;
  raw?: boolean;
  required?: boolean;
  errorMessage?: string;
}

const MAX_DECIMAL_PART_LENGTH = 18;

const limitDecimalPart = DecimalPartLimiter(MAX_DECIMAL_PART_LENGTH);

const PercentageInput: FC<PercentageInputProps> = ({
  value,
  labelTitle,
  onFocus,
  onBlur,
  onChange,
  placeholder,
  readOnly,
  disabled,
  showLoader,
  loaderState,
  onLoaderStateReset,
  tabIndex,
  inputIcon,
  raw = false,
  required,
  errorMessage,
}) => {
  const percentageValue = numberToPercentage(value);
  const [inputValue, setInputValue] = useState(percentageValue);
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setInputValue(percentageValue);
    setIsFocused(true);

    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    onChange(percentageToNumber(inputValue));
    setIsFocused(false);

    onBlur?.(event);
  };

  const handleChange = (val: string) => {
    setInputValue(val);
    onChange(percentageToNumber(val));
  };

  const visualValue = isFocused ? inputValue : percentageValue;

  const [maskedValue, inputRef, handleInputChange, handleKeyDown, handleKeyUp, handleClick] = useMask(
    visualValue,
    (val: string) => handleChange(limitDecimalPart(val)),
    maskNumberValue,
    removeNonNumericChars,
    (char: string) => char === ',',
  );

  return (
    <div className={styles.container}>
      <div className={clsx(styles.dataTypeContainer, errorMessage && styles.errorDataTypeContainer)}>
        <PercentageImage />
      </div>
      <TextInput
        value={raw ? value : maskedValue}
        labelTitle={labelTitle}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onClick={handleClick}
        inputRef={inputRef}
        hasLeftPadding
        hasRightNeighbour
        readOnly={readOnly}
        disabled={disabled}
        placeholder={placeholder}
        loaderState={loaderState}
        onLoaderStateReset={onLoaderStateReset}
        showLoader={showLoader}
        tabIndex={tabIndex}
        inputIcon={inputIcon}
        required={required}
        errorMessage={errorMessage}
      />
    </div>
  );
};

export default PercentageInput;
